import { get } from "svelte/store";
import { projectSettings } from "../config";
import model from "../stores/modelStore";

window.gtm_var_disable_tealium = window.gtm_var_disable_tealium || false;
window.gtm_var_web_visitor_id = window.gtm_var_web_visitor_id || false;

function getCookie(name) {
	var value = "; " + document.cookie;
	var parts = value.split("; " + name + "=");
	if (parts.length == 2) {
		return parts.pop().split(";").shift();
	}
	return null;
}

function userIdentifier(attempts) {
	attempts = attempts || 0;
	var maxAttempts = 20;

	try {
		// allow me to provide an override via GTM.
		var gtmIdOverride = window.gtm_var_web_visitor_id;
		if (window.gtm_var_web_visitor_id) {
			return window.gtm_var_web_visitor_id;
		}

		// get GA4 cookie.
		var ga4Cookie = getCookie("_ga");
		if (ga4Cookie) {
			// get the correct value from cookie:
			var uid = /.+\.(\d+\.\d+)$/gi.exec(ga4Cookie)[1];
			// add required prefix:
			var prefix = "ga_c_id|" + uid;
			return prefix;
		} else if (attempts < maxAttempts) {
			setTimeout(function () {
				userIdentifier(attempts + 1);
			}, 500);
		} else {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "ga4_id_capture_timeout"
			});
		}
	} catch (err) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: "ga4_id_capture_error"
		});
	}
}

export const getTealiumID = () =>
	typeof window !== "undefined" && typeof window.utag === "object" && typeof window.utag.data === "object"
		? window.utag.data.tealium_visitor_id
		: null;

const formatPhoneNumber = (phoneNumber) => {
	if (phoneNumber) {
		let formattedNumber = phoneNumber.replace(/\s+/g, "");
		if (formattedNumber.startsWith("0")) {
			return formattedNumber.replace(/^0/, "+61");
		} else if (!formattedNumber.startsWith("+61")) {
			return "+61" + formattedNumber;
		}
		return formattedNumber;
	}
	return phoneNumber;
};

async function hash(string) {
	const utf8 = new TextEncoder().encode(string);
	const hashBuffer = await crypto.subtle.digest("SHA-256", utf8);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashHex = hashArray.map((bytes) => bytes.toString(16).padStart(2, "0")).join("");
	return hashHex;
}

const pushDataLayer = async (data) => {
	window.dataLayer = window.dataLayer || [];
	if (data.tealium_event) {
		data.event = data.tealium_event;
		delete data.tealium_event;
	}

	window.dataLayer.push(data);
};

// Helper function that assists in asynchronously sending user data just in case
// the user interacts with the page before the Tealium scripts load. Prevents data being dropped.
const resolveUtag = async () => {
	if (window?.utag) return window?.utag;

	await new Promise((resolve) => setTimeout(resolve, 200));
	if (window?.utag) return window?.utag;

	return { link: () => undefined, view: () => undefined };
};

const utagLink = async (data) => {
	// If Tealium has been disabled, don't send any data.
	if (window.gtm_var_disable_tealium) return;

	let utag = await resolveUtag();
	utag.link(data);
};

const registerFormStart = () => {
	const data = {
		class_action_name: projectSettings.classActionName,
		page_url: `${window.location.hostname}${window.location.pathname}`
	};
	const eventName = `class_action_form_start`;

	pushDataLayer({
		...data,
		event: eventName
	});
	utagLink({
		...data,
		tealium_event: eventName
	});
};

const getEmailDataPoints = async (emailAddress) => {
	let email = null;
	let emailHash = null;
	let emailHashGads = null;

	if (Array.isArray(emailAddress) && emailAddress.length > 0) {
		email = emailAddress[0];
	} else {
		email = emailAddress;
	}

	if (email) {
		emailHash = await hash(email.toLowerCase().trim());

		if (email.endsWith("@gmail.com") || email.endsWith("@googlemail.com")) {
			let arr = email.toLowerCase().trim().split("@");
			if (arr.length == 2) {
				let name = arr[0].replace(".", "");
				emailHashGads = await hash(`${name}@${arr[1]}`);
			}
		}
	}

	return { emailHash, emailHashGads };
};

const getPhoneDataPoints = async (phoneNumber) => {
	if (Array.isArray(phoneNumber) && phoneNumber.length > 0) {
		return await hash(phoneNumber[0]);
	} else {
		return await hash(phoneNumber);
	}
};

const getAdditionalDataPoints = async (model, data) => {
	let emails = { emailHash: null, emailHashGads: null };
	let phoneHash = null;

	if (model.contact) {
		let phone = model.contact.phoneNumber || model.contact.phoneNumbers;
		let formattedPhoneNumber = formatPhoneNumber(phone);
		emails = await getEmailDataPoints(model.contact.emailAddress || model.contact.emailAddresses, data);
		phoneHash = await getPhoneDataPoints(formattedPhoneNumber);
	} else if (model.Contact) {
		let phone = model.Contact.PhoneNumber || model.Contact.PhoneNumbers;
		let formattedPhoneNumber = formatPhoneNumber(phone);
		emails = await getEmailDataPoints(model.Contact.EmailAddress || model.Contact.EmailAddresses, data);
		phoneHash = await getPhoneDataPoints(formattedPhoneNumber);
	}

	const registrationType = model.registrationType || model.RegistrationType || null;

	if (emails.emailHash) data.hashed_email = emails.emailHash;
	if (emails.emailHashGads) data.hashed_email_gads = emails.emailHashGads;
	if (phoneHash) data.hashed_phone = phoneHash;
	if (registrationType) data.class_action_behalf_of = registrationType;

	return data;
};

const registerPageComplete = async (pageName) => {
	model.update((m) => {
		if (window.gtm_var_disable_tealium) {
			let webVisitorId = window.gtm_var_web_visitor_id || userIdentifier();
			m.tealiumId = webVisitorId;
		} else {
			m.tealiumId = getTealiumID();
		}

		return m;
	});

	let data = {
		class_action_name: projectSettings.classActionName,
		class_action_phase_name: pageName,
		page_url: `${window.location.hostname}${window.location.pathname}`
	};

	data = await getAdditionalDataPoints(get(model), data);

	const eventName = `class_action_form_phase_completed`;

	pushDataLayer({
		...data,
		event: eventName
	});

	utagLink({
		...data,
		tealium_event: eventName
	});
};

const registerSubmit = async (email, phone, registrationNumber) => {
	let data = {
		class_action_name: projectSettings.classActionName,
		page_url: `${window.location.hostname}${window.location.pathname}`,
		reference_number: registrationNumber
	};

	const formattedPhoneNumber = formatPhoneNumber(phone);
	const phoneHash = await hash(formattedPhoneNumber);
	data.hashed_phone = phoneHash;

	const emails = await getEmailDataPoints(email);
	if (emails.emailHash) data.hashed_email = emails.emailHash;
	if (emails.emailHashGads) data.hashed_email_gads = emails.emailHashGads;

	const eventName = `class_action_form_submit`;

	pushDataLayer({
		...data,
		event: eventName
	});

	utagLink({
		...data,
		tealium_event: "class_action_form_submit"
	});
};

const regFormPhone = async (phoneNumber) => {
	let formattedPhoneNumber = formatPhoneNumber(phoneNumber);

	var data = {
		click_text: "Call " + formattedPhoneNumber,
		click_url: "tel:" + formattedPhoneNumber,
		event_action: "user_click",
		tealium_event_type: "link",
		event: "tel_link_click"
	};

	pushDataLayer(data);
};

const regFormEmail = async (emailAddress) => {
	var data = {
		click_text: emailAddress,
		click_url: "mailto:" + emailAddress,
		event_action: "user_click",
		tealium_event_type: "link",
		event: "email_link_click"
	};

	pushDataLayer(data);
};

export { registerFormStart, registerPageComplete, registerSubmit, pushDataLayer, regFormEmail, regFormPhone };
